import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { X, Sparkles, Loader2, Check, Copy } from "lucide-react";

export default function MailingListPopup() {
  const [show, setShow] = useState(false);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [confirmMsg, setConfirmMsg] = useState("");
  const [confirmHeadline, setConfirmHeadline] = useState("You're In.");
  const [discountCode, setDiscountCode] = useState("");
  const [copied, setCopied] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const trackFunnel = (event) => {
    try {
      let sid = localStorage.getItem("fleuris_funnel_sid");
      if (!sid) {
        sid = (crypto.randomUUID?.() || String(Date.now()) + Math.random());
        localStorage.setItem("fleuris_funnel_sid", sid);
      }
      axios.post(`${API}/funnel/event`, { event, session_id: sid }).catch(() => {});
    } catch { /* tracking is best-effort */ }
  };

  // Don't interrupt conversion-critical flows
  const suppressed = ["/memberships", "/collection", "/admin"].some((p) => location.pathname.startsWith(p));

  useEffect(() => {
    if (suppressed) return;
    // Already joined (persistent) or dismissed this session → never show again
    if (localStorage.getItem("fleuris_inner_circle_joined")) return;
    if (sessionStorage.getItem("fleuris_popup_dismissed")) return;
    const timer = setTimeout(() => {
      setShow(true);
      trackFunnel("popup_view");
    }, 8000);
    return () => clearTimeout(timer);
  }, [suppressed]);

  const handleClose = () => {
    setShow(false);
    sessionStorage.setItem("fleuris_popup_dismissed", "true");
    if (!confirmed) trackFunnel("popup_dismiss");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    trackFunnel("popup_start");
    try {
      const res = await axios.post(`${API}/subscribe/noir-index`, {
        email,
        origin_url: window.location.origin,
        source: "Website - Inner Circle - 15% Welcome Offer",
      });
      // Persist so subscribers never see the popup again
      localStorage.setItem("fleuris_inner_circle_joined", "true");
      if (res.data.discount_code) {
        setDiscountCode(res.data.discount_code);
        try { localStorage.setItem("fleuris_welcome_code", res.data.discount_code); } catch { /* ignore */ }
      }
      if (res.data.already_subscribed) {
        setConfirmHeadline("You're Already In.");
        setConfirmMsg(
          res.data.welcome_resent
            ? "Here's your 15% off code again — enjoy it on your first order."
            : "Your Fleuris Noir welcome offer was previously sent to this email address."
        );
      } else {
        setConfirmHeadline("You're In.");
        setConfirmMsg("Here's your 15% off code for your first order — we've also emailed it to you.");
        trackFunnel("popup_complete");
      }
      setConfirmed(true);
    } catch {
      setConfirmHeadline("Something Went Wrong.");
      setConfirmMsg("Please try again in a moment.");
      setConfirmed(true);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = () => {
    setShow(false);
    navigate("/collection");
  };

  if (!show || suppressed) return null;

  return (
    <>
      <div className="fixed inset-0 bg-[#0c0c0c]/70 backdrop-blur-sm z-[95]" onClick={handleClose} />
      <div className="fixed inset-0 z-[96] flex items-center justify-center p-4" data-testid="mailing-popup">
        <div className="bg-[#161616] border border-[#b8966a]/20 w-full max-w-md p-8 md:p-10 relative animate-fade-in">
          <button onClick={handleClose} className="absolute top-4 right-4 text-[#a19d98] hover:text-[#f8f5f0] transition-colors" data-testid="popup-close">
            <X size={20} />
          </button>

          {confirmed ? (
            <div className="text-center" data-testid="popup-confirmation">
              <Check className="text-[#b8966a] mx-auto mb-4" size={24} />
              <h2 className="font-display text-2xl text-[#f8f5f0] mb-2">{confirmHeadline}</h2>
              <p className="font-body text-[#a19d98] mb-6 leading-relaxed">{confirmMsg}</p>
              {discountCode && (
                <div className="mb-6" data-testid="popup-discount-block">
                  <p className="font-body text-[10px] tracking-[0.3em] uppercase text-[#b8966a] mb-2">Your 15% Off Code</p>
                  <button
                    type="button"
                    onClick={() => {
                      navigator.clipboard?.writeText(discountCode).catch(() => {});
                      setCopied(true);
                      setTimeout(() => setCopied(false), 1800);
                    }}
                    className="w-full border border-[#b8966a]/40 hover:border-[#b8966a] px-4 py-4 flex items-center justify-center gap-3 transition-colors group"
                    data-testid="popup-discount-code"
                  >
                    <span className="font-display text-xl tracking-[0.2em] text-[#f8f5f0]">{discountCode}</span>
                    {copied ? <Check size={16} className="text-[#b8966a]" /> : <Copy size={16} className="text-[#a19d98] group-hover:text-[#b8966a]" />}
                  </button>
                  <p className="font-body text-xs text-[#a19d98]/70 mt-2">
                    {copied ? "Copied!" : "Tap to copy · apply at checkout for 15% off your first order"}
                  </p>
                </div>
              )}
              <button onClick={handleContinue} className="btn-primary w-full" data-testid="popup-continue">
                Continue Shopping
              </button>
            </div>
          ) : (
            <div className="text-center">
              <Sparkles className="text-[#b8966a] mx-auto mb-4" size={24} />
              <h2 className="font-display text-2xl text-[#f8f5f0] mb-2">Join the Inner Circle</h2>
              <p className="font-body text-[#a19d98] mb-6 leading-relaxed">
                Be the first to know about exclusive collections, events, and more — plus enjoy 15% off your first order as our welcome gift.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="your@email.com"
                  className="w-full bg-[#0c0c0c] border border-[#b8966a]/30 px-4 py-3 text-[#f8f5f0] font-body focus:border-[#b8966a] transition-colors text-center"
                  data-testid="popup-email"
                />
                <button type="submit" disabled={loading}
                  className="btn-primary w-full flex items-center justify-center gap-2 disabled:opacity-50" data-testid="popup-submit">
                  {loading ? <Loader2 className="animate-spin" size={14} /> : "Get My 15% Off"}
                </button>
              </form>

              <p className="font-body text-xs text-[#a19d98]/60 mt-4">
                Exclusive collections, events & more. Unsubscribe anytime.
              </p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}
